import { Injectable, inject } from '@angular/core';

import { IConstant, IMarket, ISportEvent, ITypeSegment } from '@frontend/oxygen/shared-utils';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import * as _ from 'underscore';

import { EnvironmentConfig } from '../../../client-config/index';
import { ICoupon } from '../../../core/models/coupon.model';
import { CmsService } from '../../../core/services/cms/cms.service';
import { ISystemConfig } from '../../../core/services/cms/models';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { GtmService } from '../../../core/services/gtm/gtm.service';
import { SiteServerService } from '../../../core/services/siteServer/site-server.service';
import { GamingService } from '../../../core/services/sport/gaming.service';
import { TimeService } from '../../../core/services/time/time.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { IBetFilterParams } from '../../../retail/services/betFilterParams/bet-filter-params.model';
import { BetFilterParamsService } from '../../../retail/services/betFilterParams/bet-filter-params.service';
import { ICouponMarketMapping, ICouponMarketSelector, IMarketSelectorConfig } from '../../../shared/components/marketSelector/market-selector.model';
import { FOOTBALL_COUPONS } from './coupons-details.constant';

@Injectable({
    providedIn: 'root',
})
export class CouponsDetailsService {
    footballCoupons: IMarketSelectorConfig;
    isGoalscorerCoupon: boolean = false;
    isCorrectScoreCoupon: boolean = false;
    private readonly environment = inject(EnvironmentConfig).configuration;
    sportId: string = this.environment?.CATEGORIES_DATA?.footballId || '';
    marketSelectorArray: ICouponMarketSelector[] = [];
    initialCoupons: any[] = [];
    index = -1;
    initialCouponCount = 15;
    postCouponCount = 100;

    private readonly defaultFootballCouponsMarkets: ICouponMarketSelector[];
    private readonly FOOTBALL_COUPONS = FOOTBALL_COUPONS;
    isCMSCouponsMapping: boolean;
    getCouponMarketSelectors: ICouponMarketSelector[];

    constructor(
        private gtmService: GtmService,
        private siteServerService: SiteServerService,
        private timeService: TimeService,
        private betFilterParamsService: BetFilterParamsService,
        private cmsService: CmsService,
        private windowRef: WindowRefService,
        private pubSubService: PubSubService,
        private gameService: GamingService,
    ) {
        this.footballCoupons = this.FOOTBALL_COUPONS;
        this.defaultFootballCouponsMarkets = this.FOOTBALL_COUPONS.DEFAULT_MARKETS;
    }

    get isCustomCoupon(): boolean {
        return this.isCorrectScoreCoupon || this.isGoalscorerCoupon;
    }
    set isCustomCoupon(value: boolean) {}

    get isCMSCoupons(): boolean {
        return this.isCMSCouponsMapping;
    }
    set isCMSCoupons(value: boolean) {}

    getCouponEvents(
        couponId: string,
        couponName: string,
        footballService: GamingService,
        cmsMappingFlag: boolean,
    ): Observable<{ coupons: ISportEvent[]; options: ICouponMarketSelector[] }> {
        const requestParams = footballService.couponEventsRequestParams(couponId);
        this.initialCoupons = [];
        this.sportId = requestParams.categoryId;
        return from(footballService.couponEventsByCouponId(_.extend({}, requestParams, { couponId }))).pipe(
            switchMap((coupons: ISportEvent[]) => {
                this.initialCoupons = coupons;
                const couponEvents = this.loadCouponEvents(this.initialCoupons);
                this.isCMSCouponsMapping = cmsMappingFlag;
                this.isCorrectScoreCoupon = this.isCorrectScoreMarkets(coupons);
                return this.isCustomCoupon
                    ? of({ coupons, options: [] })
                    : this.isCMSCoupons
                      ? this.getCMSCouponMarketSelectorForMapping(couponId, couponEvents)
                      : this.getEventById(couponEvents);
            }),
        );
    }

    isBetFilterEnable(coupon: ICoupon): Observable<boolean> {
        const params: IBetFilterParams = {};
        if (coupon.couponSortCode === 'MR') {
            params.couponName = coupon.name;
            params.mode = 'online';
            params.pathname = this.windowRef.nativeWindow.location.pathname;
            this.betFilterParamsService.betFilterParams = params;
            return this.cmsService.getSystemConfig().pipe(
                map((config: ISystemConfig) => {
                    return !!(config.Connect && config.Connect.footballFilter);
                }),
            );
        }
        return of(false);
    }

    /**
     * pushToGTM()
     */
    pushToGTM(couponName: string): void {
        const eventLabel = couponName.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1));

        this.gtmService.push('trackEvent', {
            eventCategory: 'Coupon Selector',
            eventAction: 'Select Coupon',
            eventLabel,
        });
    }

    setOddsHeader(selectOptions: ICouponMarketSelector[], marketFilter: string): string[] {
        if (selectOptions && selectOptions.length) {
            const headerObj = selectOptions.find(
                (option: ICouponMarketSelector) => option.templateMarketName.toLowerCase() === marketFilter.toLowerCase(),
            );
            return headerObj && headerObj.header ? headerObj.header : [];
        }
        return [];
    }

    /**
     * Group Coupon Events
     * @param {ISportEvent[]} data
     * @returns {boolean[]}
     */
    groupCouponEvents(data: ISportEvent[], footballService: GamingService): ITypeSegment[] {
        return _.uniq(
            _.chain(
                _.map(_.groupBy(data, 'typeName'), (events: ISportEvent[]) => {
                    return footballService.arrangeEventsBySection(events, !this.isCustomCoupon)[0];
                }),
            )
                .sortBy('classDisplayOrder')
                .sortBy('typeDisplayOrder')
                .value(),
        );
    }

    /**
     * Check if quick bet is blocked on coupons details page
     * @returns {Observable<boolean>}
     */
    isQuickBetBlocked(): Observable<boolean> {
        return this.cmsService.getSystemConfig().pipe(map((config: ISystemConfig) => config.quickBet && config.quickBet.blockOnCouponDetailsPage));
    }

    /**
     * Filter event with same property
     * @param array
     * @param property
     * @param childKey
     */
    filteredArr(array: any[] = [], property: string, childKey: string = ''): any[] {
        return array.reduce((acc, current) => {
            const sameProperty = acc.find((item: IConstant) => item[property as keyof IConstant] === current[property]);
            if (sameProperty && sameProperty[childKey]) {
                sameProperty[childKey] = this.filteredArr(current[childKey].concat(sameProperty[childKey]), property);
            }
            return !sameProperty ? acc.concat([current]) : acc;
        }, []);
    }

    getEventById(coupons: ISportEvent[]): Observable<any> {
        let cmsOptions: ICouponMarketSelector[] = [];
        let options: ICouponMarketSelector[] = [];
        options = this.getMarketOptions(options, this.marketSelectorArray, coupons); //// have to remove this
        const ids = coupons.map((match) => match.id);
        const params = {
            eventsIds: ids,
            isStarted: false,
            marketsCount: false,
            childCount: true,
            suspendAtTime: this.timeService.getSuspendAtTime(),
            templateMarketNameOnlyIntersects: true,
        };
        return from(this.siteServerService.getEventsByEventsIds(params)).pipe(
            switchMap((events: ISportEvent[]) => {
                coupons = events && events.length ? this.filteredArr(events.concat(coupons), 'id', 'markets') : coupons;
                cmsOptions = this.marketSelectorArray;
                options = this.getMarketOptions(options, cmsOptions, coupons);
                if (!options.length) options = this.FOOTBALL_COUPONS.EXCLUSIVE_MARKETS;
                return of({ coupons, options });
            }),
        );
    }

    private getCoupons(couponEvents: ITypeSegment[], count: number): ISportEvent[] {
        let i = 0;
        const coupons: ISportEvent[] = [];
        couponEvents.forEach((coupon: ITypeSegment, couponIndex: number) => {
            if (couponIndex > this.index && i <= count) {
                coupons.push(...coupon.events);
                i = i + coupon?.events.length;
                this.index = couponIndex;
            }
        });
        return coupons || [];
    }
    loadCouponEvents(coupons: ISportEvent[]) {
        const savedCouponEvents = this.groupCouponEvents(coupons, this.gameService);
        const couponEvents = savedCouponEvents.slice();
        return this.index == -1 ? this.getCoupons(couponEvents, this.initialCouponCount) : this.getCoupons(couponEvents, this.postCouponCount);
    }

    getMarketSelector(): Observable<ICouponMarketSelector[]> {
        return this.cmsService.getCouponMarketSelector().pipe(
            map((data: ICouponMarketSelector[]) => {
                this.marketSelectorArray = data;
                return data;
            }),
        );
    }

    public getCMSCouponMarketSelectorForMapping(couponId: string, coupons: ISportEvent[]): Observable<any> {
        this.pubSubService.publish('COUPON_MARKET_SELECTOR', true);
        return this.cmsService.getToggleStatus('FootballCoupons').pipe(
            switchMap((toggleStatus: boolean) => {
                const ids = coupons.map((match) => match.id);
                const params = {
                    eventsIds: ids,
                    isStarted: false,
                    marketsCount: false,
                    childCount: true,
                    suspendAtTime: this.timeService.getSuspendAtTime(),
                    templateMarketNameOnlyIntersects: true,
                };

                return from(this.siteServerService.getEventsByEventsIds(params)).pipe(
                    map((events: ISportEvent[]) => {
                        const couponsEventsMap = new Map();
                        events.forEach((event) => couponsEventsMap.set(event.id, event.marketsCount));
                        coupons.forEach((coupon) => {
                            if (couponsEventsMap.get(coupon.id)) {
                                coupon.marketsCount = couponsEventsMap.get(coupon.id);
                            }
                        });
                        return { coupons, options: [] };
                    }),
                );
            }),
        );
    }

    /**
     * Check if events have Correct Score Markets
     * @param {ISportEvent[]} events
     * @returns {boolean}
     */
    private isCorrectScoreMarkets(events: ISportEvent[]): boolean {
        const markets = events && _.flatten(_.pluck(events, 'markets'));
        const csMarkets = _.where(markets, { dispSortName: 'CS' });
        return csMarkets && markets.length === csMarkets.length;
    }

    private getMarketOptions(
        options: ICouponMarketSelector[] = [],
        cmsOptions: ICouponMarketSelector[] = [],
        coupons: ISportEvent[],
    ): ICouponMarketSelector[] {
        this.modifyTemplateName(coupons);
        const marketOptions = this.filteredArr(options.concat(cmsOptions), 'templateMarketName');
        const couponsMarketsNames = this.getOpenBetMarketNames(coupons);
        const availableMarkets = this.filterMarketOptions(marketOptions, couponsMarketsNames);
        const availableMarketsNames = availableMarkets.map((market) => market.templateMarketName);
        const defaultMarketsOptions = this.filterDefaultOptions(couponsMarketsNames, availableMarketsNames);

        return availableMarkets.concat(defaultMarketsOptions);
    }

    /**
     * Modify Template Name for markets witch has rawHandicapValue attr
     * @param {ITypeSegment[]} events
     */
    private modifyTemplateName(events: ISportEvent[]): void {
        events.forEach((event: ISportEvent) => {
            event?.markets?.forEach((market: IMarket) => {
                if (market.rawHandicapValue && !/[0-9]/g.test(market.templateMarketName)) {
                    market.templateMarketName = `${market.templateMarketName} ${market.rawHandicapValue}`;
                }
            });
        });
    }

    private getOpenBetMarketNames(events: ISportEvent[]): string[] {
        const markets = _.flatten(_.pluck(events, 'markets'));
        const names = _.uniq(_.flatten(_.pluck(markets, 'templateMarketName')));
        return names.map((name) => name && name.toLowerCase());
    }

    private filterMarketOptions(cmsCouponMarkets: ICouponMarketSelector[], couponsMarketsNames: string[]): ICouponMarketSelector[] {
        const defaultMarketsNames = this.defaultFootballCouponsMarkets
            .filter((market) => this.matchName(couponsMarketsNames, market.templateMarketName) || market.isDefault)
            .map((market) => market.templateMarketName.toLowerCase());

        return cmsCouponMarkets.filter((option: ICouponMarketSelector) => {
            return (
                option.isDefault ||
                this.matchName(couponsMarketsNames, option.templateMarketName) ||
                this.matchName(defaultMarketsNames, option.templateMarketName)
            );
        });
    }

    private filterDefaultOptions(allMarketsNames: string[], cmsMarketsNames: string[]): ICouponMarketSelector[] {
        const cmsMarkets = cmsMarketsNames.map((market) => market.toLowerCase());
        return this.defaultFootballCouponsMarkets.filter((market: ICouponMarketSelector) => {
            return (
                (this.matchName(allMarketsNames, market.templateMarketName) && !this.matchName(cmsMarkets, market.templateMarketName)) ||
                (market.templateMarketName === 'Match Betting' && this.matchName(allMarketsNames, 'match result'))
            );
        });
    }

    private matchName(names: string[], name: string): boolean {
        const marketName = name.toLowerCase();
        return names && names.indexOf(marketName) >= 0;
    }

    getCMSCouponMapping(): Observable<ICouponMarketMapping[]> {
        return this.cmsService.getCouponMarketMapping().pipe(
            map((data: ICouponMarketMapping[]) => {
                return data;
            }),
        );
    }
}
